/**
 * Decode an audio URL once and return high-resolution amplitude series:
 *   - peaks:  max-abs amplitude per bin (overall — drives waveform height)
 *   - low:    max-abs amplitude per bin in the low band (~< 250 Hz, kick channel)
 *   - mid:    bandpass ~1 kHz
 *   - high:   highpass ~> 2.5 kHz
 *
 * Used for both the visual waveform and as the input to the section classifier
 * — the low-band series is the cleanest signal for detecting drops /
 * breakdowns in dance music (kick drops out → breakdown; kick returns → drop).
 *
 * Bins default to 1500 — enough resolution that a 5-min track has ~1 sample
 * every 0.2 s, which is much finer than the 8-bar segments used elsewhere.
 */

import { useEffect, useState } from 'react';

export interface AudioPeaks {
  peaks: number[];
  low: number[];
  mid: number[];
  high: number[];
  duration: number;
  sampleRate: number;
  bins: number;
}

interface State {
  data: AudioPeaks | null;
  loading: boolean;
  error: Error | null;
}

const DEFAULT_BINS = 1500;

export function useAudioPeaks(audioUrl: string | null, bins: number = DEFAULT_BINS): State {
  const [state, setState] = useState<State>({ data: null, loading: false, error: null });

  useEffect(() => {
    if (!audioUrl) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, loading: true, error: null });

    (async () => {
      try {
        const AudioContextClass =
          window.AudioContext || (window as any).webkitAudioContext;
        if (!AudioContextClass) throw new Error('AudioContext unavailable');
        const ctx = new AudioContextClass();
        const resp = await fetch(audioUrl, { mode: 'cors', credentials: 'omit' });
        if (!resp.ok) throw new Error(`fetch ${resp.status}`);
        const buf = await ctx.decodeAudioData(await resp.arrayBuffer());

        const samples = buf.getChannelData(0);
        const samplesPerBin = Math.max(1, Math.floor(samples.length / bins));
        const realBins = Math.floor(samples.length / samplesPerBin);

        const overall = peakBins(samples, samplesPerBin, realBins);

        const renderBand = async (
          buildFilter: (c: OfflineAudioContext) => AudioNode,
        ): Promise<Float32Array> => {
          const offline = new OfflineAudioContext(1, buf.length, buf.sampleRate);
          const src = offline.createBufferSource();
          src.buffer = buf;
          const filter = buildFilter(offline);
          src.connect(filter);
          filter.connect(offline.destination);
          src.start(0);
          return (await offline.startRendering()).getChannelData(0);
        };

        const lowSamples = await renderBand((c) => {
          const f = c.createBiquadFilter();
          f.type = 'lowpass';
          f.frequency.value = 250;
          f.Q.value = 0.707;
          return f;
        });
        const midSamples = await renderBand((c) => {
          const f = c.createBiquadFilter();
          f.type = 'bandpass';
          f.frequency.value = 1000;
          f.Q.value = 0.6;
          return f;
        });
        const highSamples = await renderBand((c) => {
          const f = c.createBiquadFilter();
          f.type = 'highpass';
          f.frequency.value = 2500;
          f.Q.value = 0.707;
          return f;
        });

        if (cancelled) return;
        setState({
          data: {
            peaks: overall,
            low: peakBins(lowSamples, samplesPerBin, realBins),
            mid: peakBins(midSamples, samplesPerBin, realBins),
            high: peakBins(highSamples, samplesPerBin, realBins),
            duration: buf.duration,
            sampleRate: buf.sampleRate,
            bins: realBins,
          },
          loading: false,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        setState({ data: null, loading: false, error: err as Error });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [audioUrl, bins]);

  return state;
}

function peakBins(samples: Float32Array, samplesPerBin: number, bins: number): number[] {
  const out: number[] = new Array(bins);
  let maxOverall = 0;
  for (let i = 0; i < bins; i++) {
    const start = i * samplesPerBin;
    const end = Math.min(samples.length, start + samplesPerBin);
    let max = 0;
    for (let j = start; j < end; j++) {
      const a = Math.abs(samples[j]);
      if (a > max) max = a;
    }
    out[i] = max;
    if (max > maxOverall) maxOverall = max;
  }
  if (maxOverall > 0) {
    for (let i = 0; i < bins; i++) out[i] /= maxOverall;
  }
  return out;
}
